import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useCourseFilter } from '../context/CourseFilterContext';
import {
  TRAINING_LIST_DEFAULTS,
  TRAINING_SORT_FIELDS,
  buildTrainingListCacheKey,
  buildTrainingListQuery,
  getNextTrainingSort,
  sortTrainingsLocally,
} from './trainingListApi';
import {
  Btn, Tag,
  Skeleton,
  StatGrid, Stat, StatTop, StatIco, StatDelta, StatLabel, StatValue,
  CellPrimary,
} from '../ui/primitives';
import DataTable from '../ui/DataTable';
import GroupedRowActionSheet, { ICON_PROPS } from '../ui/GroupedRowActionSheet';
import { UserPlus, Bell, Settings, Video, Pencil, Trash2, Plus, Sparkles } from 'lucide-react';
import PageActionsMenu, {
  ActionsMenuItem,
  ActionsSection,
} from '../ui/PageActionsMenu';
import { getCache, setCache, clearCacheByPrefix } from '../ui/listCache';
import DeleteConfirmDialog from '../ui/DeleteConfirmDialog';
import DashboardNavbar from '../dashboardNavbar';
import FormDialog from './dialog';
import AddQuestionDialogBox from './AddQuestionDialogBox';
import AssignStudentDialog from './assignStudentDialog';
import { LabelWithHelp } from '../ui/FieldHelp';
import { TRAINING_LIST_FIELD_HELP as HELP } from '../ui/listScreenFieldHelp';
import { Button, Typography } from '@mui/material';
import {
  MdSecurity, MdAccessTime,
  MdFlag, MdDescription, MdAutoAwesome,
} from 'react-icons/md';
import { FiPlus, FiShield } from 'react-icons/fi';
import TrainingSettingDialog from './TrainingSettingDialog';
import TrainingVideoDialog from './TrainingVideoDialog';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/* ─── presentation-only helpers ───────────────────────────────────── */

const computeStatus = (row) => {
  // Fully-completed learners take priority over the due-date derived status.
  if (Number(row.completionPercent) >= 100) return 'complete';
  if (!row.completionDate) return 'inprogress';
  const due = new Date(parseInt(row.completionDate));
  if (isNaN(due.getTime())) return 'inprogress';
  return due < new Date() ? 'overdue' : 'inprogress';
};

const formatDue = (timestampString) => {
  if (!timestampString) return '—';
  const ts = new Date(parseInt(timestampString));
  if (isNaN(ts.getTime())) return '—';
  return ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ─── styled components (presentation only) ──────────────────────── */

const PageShell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 48px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) { padding: 0 14px 28px; }
`;

const Breadcrumb = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .09em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSubtle};
  margin-bottom: 4px;
`;

const PageHero = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.space} auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroLeft = styled.div`
  h2 {
    margin: 0;
    color: ${({ theme }) => theme.text};
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 22px;
    font-weight: 760;
    letter-spacing: -.02em;
  }
  p {
    margin: 4px 0 0;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
  }
`;

const ContentWrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
`;

const ProgressBar = styled.div`
  height: 6px;
  border-radius: 4px;
  background: ${({ theme }) => theme.surface3};
  overflow: hidden;
  min-width: 100px;
  box-shadow: inset 0 0 0 1px rgba(17,20,38,.04);
  > i {
    display: block;
    height: 100%;
    border-radius: 4px;
    width: ${({ $pct }) => Math.min(Math.max($pct || 0, 0), 100)}%;
    ${({ $status, theme }) => $status === 'complete' && css`
      background: linear-gradient(90deg, ${theme.success}, color-mix(in oklch, ${theme.success} 80%, #fff));
    `}
    ${({ $status, theme }) => $status === 'inprogress' && css`
      background: linear-gradient(90deg, ${theme.accent}, ${theme.accentStrong});
    `}
    ${({ $status, theme }) => $status === 'overdue' && css`
      background: linear-gradient(90deg, ${theme.danger}, color-mix(in oklch, ${theme.danger} 80%, #000));
    `}
  }
`;

const CompletionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const CompletionFraction = styled.span`
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textMuted};
  font-variant-numeric: tabular-nums;
`;

const ProgramCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProgramIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  flex: none;
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accentStrong};
  box-shadow: inset 0 0 0 1px ${({ theme }) => `color-mix(in oklch, ${theme.accent} 22%, transparent)`};
  svg { width: 15px; height: 15px; }
`;

const initialValue = { trainingId: '', courseName: '', startDate: '', completionDate: '' };

/* ─── component ──────────────────────────────────────────────────── */

const Training = () => {
  /* ── existing state & logic (unchanged) ─────────────────────── */
  const apiUrl = process.env.REACT_APP_API_URL;
  const [tableData, setTableData] = useState(null);
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [formData, setFormData] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const userId = localStorage.getItem('userId');
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const navigate = useNavigate();
  const userType = localStorage.getItem('userType');
  const [openAddQuestion, setOpenAddQuestion] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false, count: 1, action: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTraining, setSettingsTraining] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTraining, setAssignTraining] = useState(null);
  const { isLocked, guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();

  // Client-side column sort (no API call / cache key impact — see table-ux.md).
  const [trainingSortBy, setTrainingSortBy] = useState(TRAINING_LIST_DEFAULTS.sortBy);
  const [trainingSortDir, setTrainingSortDir] = useState(TRAINING_LIST_DEFAULTS.sortDir);

  const handleTrainingSortChange = (columnKey) => {
    const next = getNextTrainingSort(columnKey, trainingSortBy, trainingSortDir);
    setTrainingSortBy(next.sortBy);
    setTrainingSortDir(next.sortDir);
  };
  // Course filter now lives in the global TopBar dropdown via shared context.
  // selectedCourseId === '' means "All courses" (show every training).
  // Filtering is server-side: courseId is sent to GET /api/trainings/{userId}.
  const { selectedCourseId } = useCourseFilter();

  useEffect(() => {
    const planExpire = localStorage.getItem('planExpire');
    if (planExpire === '1' && userType != 2) {
      sessionStorage.removeItem('planRenewDismissed');
      navigate('/Portal/Instructor');
    }
  }, [navigate]);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    return `Basic ${btoa(`${username}:${password}`)}`;
  };

  const openVideoDialog = (url) => { setVideoUrl(url); setVideoDialogOpen(true); };
  const closeVideoDialog = () => { setVideoDialogOpen(false); setVideoUrl(null); };

  // Cache-first load keyed by user + course filter (mirrors enrollments).
  const fetchTrainings = useCallback(() => {
    if (!userId) return;

    const cacheKey = buildTrainingListCacheKey(userId, { courseId: selectedCourseId });
    const cached = getCache(cacheKey);
    if (cached) {
      setTableData(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    const query = buildTrainingListQuery({ courseId: selectedCourseId });
    const urlSuffix = query ? `?${query}` : '';

    fetch(`${apiUrl}/api/trainings/${userId}${urlSuffix}`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
        'Access-Control-Allow-Origin': '*',
        Authorization: getBasicAuthHeader(),
      },
    })
      .then(resp => resp.json())
      .then(resp => {
        setTableData(resp);
        setCache(cacheKey, resp);
      })
      .catch(err => console.error('Error fetching trainings:', err))
      .finally(() => setLoading(false));
  }, [apiUrl, userId, selectedCourseId]);

  // Re-fetch whenever the global course filter changes (server-side filtering).
  useEffect(() => {
    fetchTrainings();
  }, [fetchTrainings]);

  const askConfirm = (count, action) => setConfirmState({ open: true, count, action });
  const closeConfirm = () => setConfirmState({ open: false, count: 1, action: null });
  const runConfirm = async () => {
    const action = confirmState.action;
    closeConfirm();
    if (action) await action();
  };

  const handleClickOpen = () => {
    if (!guardAction('action.training.create')) return;
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setFormData(initialValue); };
  const handleAddQuestionDialogBoxClose = () => { setOpenAddQuestion(false); setFormData(initialValue); };
  const handleCloseAlert = () => setShowAlert(false);

  const handleUpdate = (oldData) => {
    if (!guardAction('action.training.edit')) return;
    setFormData(oldData);
    setOpen(true);
  };

  const handleDelete = (id) => {
    if (!id) return;
    if (!guardAction('action.training.delete')) return;
    askConfirm(1, () => doDelete(id));
  };

  const doDelete = async (id) => {
    if (!id) return;
    try {
      const resp = await axios.delete(`${apiUrl}/api/trainings/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      if (resp.status === 200) { clearCacheByPrefix(`trainings:${userId}:`); fetchTrainings(); }
    } catch (e) {}
  };

  const handleVideo = async (trainingId) => {
    if (!guardAction('action.training.video')) return;
    try {
      const resp = await axios.get(`${apiUrl}/api/${trainingId}/video-url`, {
        headers: { Authorization: getBasicAuthHeader() },
      });
      if (resp.status === 200) openVideoDialog(resp.data);
      else openVideoDialog(null);
    } catch (e) {
      openVideoDialog(null);
    }
  };

  const handleOpenSettings = (row) => {
    if (!guardAction('action.training.settings')) return;
    setSettingsTraining(row);
    setSettingsOpen(true);
  };

  const handleOpenAssign = (row) => {
    if (!guardAction('action.training.assign')) return;
    setAssignTraining(row);
    setAssignOpen(true);
  };

  const handleCloseAssign = () => {
    setAssignOpen(false);
    setAssignTraining(null);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
    setSettingsTraining(null);
  };

  const handleFormSubmit = async (data) => {
    if (!guardAction('action.training.create.save')) return;
    // Save is an explicit action in the dialog, so we don't gate it behind a
    // typed-confirm dialog (which is redundant here and, in the MUI-dialog flows,
    // is unreachable behind the dialog focus trap).
    setLoading(true);
    const url = data.trainingId
      ? `${apiUrl}/api/trainings/${userId}/${data.trainingId}`
      : `${apiUrl}/api/trainings`;
    const method = data.trainingId ? 'PUT' : 'POST';
    fetch(url, {
      method,
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
        'Access-Control-Allow-Origin': '*',
        Authorization: getBasicAuthHeader(),
      },
    })
      .then(async resp => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.json().catch(() => ({}));
      })
      .then(() => { handleClose(); clearCacheByPrefix(`trainings:${userId}:`); fetchTrainings(); })
      .finally(() => setLoading(false));
  };

  const onChange = (e) => {
    const { value, id } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  /* ── presentation-only derived data ─────────────────────────── */

  const enrichedRows = useMemo(() => (tableData || []).map(row => ({
    ...row,
    _status: computeStatus(row),
    _type: row.price ? 'mandatory' : 'optional',
  })), [tableData]);

  const totalPrograms = enrichedRows.length;
  const mandatoryCount = enrichedRows.filter(r => r._type === 'mandatory').length;
  const overdueCount = enrichedRows.filter(r => r._status === 'overdue').length;
  const activeCount = enrichedRows.filter(r => r._status !== 'complete').length;

  // Client-side sort only — search/status filters live in DataTable (table-ux.md).
  const sortedRows = useMemo(
    () => sortTrainingsLocally(enrichedRows, trainingSortBy, trainingSortDir),
    [enrichedRows, trainingSortBy, trainingSortDir],
  );

  const statusConfig = {
    inprogress: { label: 'In progress', tone: 'amber' },
    overdue:    { label: 'Overdue',     tone: 'red'   },
    complete:   { label: 'Complete',    tone: 'green'  },
  };

  /* ─── render ─────────────────────────────────────────────────── */

  return (
    <PageShell>
      <DashboardNavbar menuItems={[]} />
      <PageHero>
        <HeroLeft>
          <Breadcrumb>Training &amp; Compliance</Breadcrumb>
          <h2>Training</h2>
          <p>
            {loading
              ? 'Loading…'
              : `${totalPrograms} program${totalPrograms !== 1 ? 's' : ''} · ${activeCount} active`}
          </p>
        </HeroLeft>
        <PageActionsMenu
          primaryLabel="New training"
          primaryIcon={<Plus size={16} strokeWidth={2.25} aria-hidden="true" />}
          onPrimaryClick={handleClickOpen}
          primaryLocked={isLocked('action.training.create')}
          menuTitle="Training tools"
          menuSubtitle="Create programs and generate modules."
        >
          {(closeActions) => (
            <ActionsSection label="Create">
              <ActionsMenuItem
                label="Generate module"
                description="AI-assisted training module draft"
                icon={<Sparkles size={15} strokeWidth={1.9} />}
                closeActions={closeActions}
                onClick={() => {
                  guardAction('action.training.generateModule', () => {
                    setAlertMessage('Generate module feature coming soon');
                    setShowAlert(true);
                  });
                }}
                {...lockProps('action.training.generateModule')}
              />
            </ActionsSection>
          )}
        </PageActionsMenu>
      </PageHero>

      <ContentWrap>
        {/* ── stat cards ─────────────────────────────────────── */}
        <StatGrid>
          {/* Overall completion */}
          <Stat>
            <StatTop>
              <StatIco><MdSecurity size={19} /></StatIco>
              {loading
                ? <Skeleton $w="52px" $h="22px" $r="20px" />
                : <StatDelta $dir="up">+6%</StatDelta>}
            </StatTop>
            <StatLabel><LabelWithHelp help={HELP.overallCompletion}>Overall completion</LabelWithHelp></StatLabel>
            <StatValue>
              {loading
                ? <Skeleton $w="70px" $h="30px" />
                : totalPrograms > 0
                  ? `${Math.round((activeCount / totalPrograms) * 100)}%`
                  : '—'}
            </StatValue>
          </Stat>

          {/* Mandatory due */}
          <Stat>
            <StatTop>
              <StatIco $tone="warning">
                <MdAccessTime size={19} />
              </StatIco>
              {loading
                ? <Skeleton $w="52px" $h="22px" $r="20px" />
                : <StatDelta $dir="down">2 soon</StatDelta>}
            </StatTop>
            <StatLabel><LabelWithHelp help={HELP.mandatoryDue}>Mandatory due</LabelWithHelp></StatLabel>
            <StatValue>
              {loading
                ? <Skeleton $w="40px" $h="30px" />
                : mandatoryCount}
            </StatValue>
          </Stat>

          {/* Overdue learners */}
          <Stat>
            <StatTop>
              <StatIco $tone="danger">
                <MdFlag size={19} />
              </StatIco>
              {loading
                ? <Skeleton $w="52px" $h="22px" $r="20px" />
                : <StatDelta $dir="up">+12</StatDelta>}
            </StatTop>
            <StatLabel><LabelWithHelp help={HELP.overdueLearners}>Overdue learners</LabelWithHelp></StatLabel>
            <StatValue>
              {loading
                ? <Skeleton $w="40px" $h="30px" />
                : overdueCount}
            </StatValue>
          </Stat>

          {/* Programs active */}
          <Stat>
            <StatTop>
              <StatIco>
                <MdDescription size={19} />
              </StatIco>
              {loading
                ? <Skeleton $w="52px" $h="22px" $r="20px" />
                : <StatDelta>—</StatDelta>}
            </StatTop>
            <StatLabel><LabelWithHelp help={HELP.programsActive}>Programs active</LabelWithHelp></StatLabel>
            <StatValue>
              {loading
                ? <Skeleton $w="40px" $h="30px" />
                : totalPrograms}
            </StatValue>
          </Stat>
        </StatGrid>

        {/* ── table card ──────────────────────────────────────── */}
        <DataTable
          rows={sortedRows}
          loading={loading}
          rowKey={(row, i) => row.trainingId ?? i}
          sortBy={trainingSortBy}
          sortDir={trainingSortDir}
          onSortChange={handleTrainingSortChange}
          search
          searchPlaceholder="Search training programs..."
          searchKeys={['trainingName', 'courseName']}
          filters={[
            { key: 'all', label: 'All', test: () => true },
            { key: 'inprogress', label: 'In progress', test: (r) => r._status === 'inprogress' },
            { key: 'overdue', label: 'Overdue', test: (r) => r._status === 'overdue' },
            { key: 'complete', label: 'Complete', test: (r) => r._status === 'complete' },
          ]}
          columns={[
            {
              header: 'Program',
              sortKey: TRAINING_SORT_FIELDS.program,
              render: (row) => (
                <ProgramCell>
                  <ProgramIcon>
                    <FiShield />
                  </ProgramIcon>
                  <CellPrimary>
                    {row.price ? `$ ${row.trainingName}` : row.trainingName}
                  </CellPrimary>
                </ProgramCell>
              ),
            },
            {
              header: <LabelWithHelp help={HELP.audience}>Audience</LabelWithHelp>,
              sortKey: TRAINING_SORT_FIELDS.audience,
              render: (row) => row.courseName || '—',
              muted: true,
            },
            {
              header: <LabelWithHelp help={HELP.completion}>Completion</LabelWithHelp>,
              sortKey: TRAINING_SORT_FIELDS.completion,
              render: (row) => {
                const pct = Number(row.completionPercent) || 0;
                return (
                  <CompletionCell>
                    <ProgressBar $pct={pct} $status={row._status}><i /></ProgressBar>
                    <CompletionFraction>{`${Math.round(pct)}%`}</CompletionFraction>
                  </CompletionCell>
                );
              },
            },
            {
              header: <LabelWithHelp help={HELP.due}>Due</LabelWithHelp>,
              sortKey: TRAINING_SORT_FIELDS.due,
              render: (row) => formatDue(row.completionDate),
              muted: true,
              mono: true,
            },
            {
              header: <LabelWithHelp help={HELP.type}>Type</LabelWithHelp>,
              sortKey: TRAINING_SORT_FIELDS.type,
              render: (row) => (
                <Tag $tone={row._type === 'mandatory' ? 'amber' : 'blue'}>
                  {row._type === 'mandatory' ? 'Mandatory' : 'Optional'}
                </Tag>
              ),
            },
            {
              header: <LabelWithHelp help={HELP.status}>Status</LabelWithHelp>,
              sortKey: TRAINING_SORT_FIELDS.status,
              render: (row) => {
                const cfg = statusConfig[row._status] || statusConfig.inprogress;
                return <Tag $tone={cfg.tone}>{cfg.label}</Tag>;
              },
            },
          ]}
          actionsColumnWidth={52}
          actions={(row) => (
            <GroupedRowActionSheet
              entityLabel="Program"
              title={row.price ? `$ ${row.trainingName}` : row.trainingName}
              menuAriaLabel="Training program actions"
              layoutKey={String(row.trainingId)}
              primary={{
                key: 'assign',
                icon: <UserPlus {...ICON_PROPS} />,
                label: 'Assign students',
                ...lockProps('action.training.assign'),
              }}
              groupLabel="For this program"
              groupItems={[
                {
                  key: 'notifications',
                  icon: <Bell {...ICON_PROPS} />,
                  label: 'Notifications',
                  ...lockProps('action.training.notifications'),
                },
                {
                  key: 'settings',
                  icon: <Settings {...ICON_PROPS} />,
                  label: 'Settings',
                  ...lockProps('action.training.settings'),
                },
                {
                  key: 'video',
                  icon: <Video {...ICON_PROPS} />,
                  label: 'Video',
                  ...lockProps('action.training.video'),
                },
              ]}
              items={[
                {
                  key: 'edit',
                  icon: <Pencil {...ICON_PROPS} />,
                  label: 'Edit',
                  ...lockProps('action.training.edit'),
                },
              ]}
              dangerItem={{
                key: 'delete',
                icon: <Trash2 {...ICON_PROPS} />,
                label: 'Delete',
                ...lockProps('action.training.delete'),
              }}
              onAction={(key) => {
                if (key === 'assign') handleOpenAssign(row);
                else if (key === 'notifications') { /* stub — unchanged */ }
                else if (key === 'settings') handleOpenSettings(row);
                else if (key === 'video') handleVideo(row.trainingId);
                else if (key === 'edit') handleUpdate(row);
                else if (key === 'delete') handleDelete(row.trainingId);
              }}
            />
          )}
          emptyText={
            sortedRows.length > 0
              ? 'No programs match your search.'
              : 'No training programs yet.'
          }
        />
      </ContentWrap>

      {/* ── alert box ─────────────────────────────────────────── */}
      {showAlert && (
        <div style={{
          position: 'fixed', top: '42%', left: '42%',
          width: '30%', background: '#CBC3E3',
          borderRadius: 10, boxShadow: '0 0 20px rgba(0,0,0,.5)',
          padding: 32, zIndex: 9999, textAlign: 'center',
        }}>
          <Typography variant="h5" gutterBottom>{alertMessage}</Typography>
          <Button variant="contained" color="primary" onClick={handleCloseAlert}>Close</Button>
        </div>
      )}

      {/* ── video dialog ──────────────────────────────────────── */}
      <TrainingVideoDialog
        open={videoDialogOpen}
        videoUrl={videoUrl}
        onClose={closeVideoDialog}
      />

      {/* ── form dialogs ─────────────────────────────────────── */}
      <FormDialog
        open={open}
        handleClose={handleClose}
        data={formData}
        onChange={onChange}
        handleFormSubmit={handleFormSubmit}
        loading={loading}
      />
      <AddQuestionDialogBox
        open={openAddQuestion}
        handleClose={handleAddQuestionDialogBoxClose}
        data={formData}
        loading={loading}
      />
      <DeleteConfirmDialog
        open={confirmState.open}
        count={confirmState.count}
        onClose={closeConfirm}
        onConfirm={runConfirm}
      />
      <TrainingSettingDialog
        open={settingsOpen}
        trainingDetails={settingsTraining}
        onClose={handleCloseSettings}
        saveLocked={isLocked('action.training.settings')}
        onSaveBlocked={() => setLockedDialogOpen(true)}
        onSaved={() => {
          clearCacheByPrefix(`trainings:${userId}:`);
          fetchTrainings();
        }}
      />
      <AssignStudentDialog
        open={assignOpen}
        selectedTrainings={assignTraining ? [assignTraining] : []}
        onClose={handleCloseAssign}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </PageShell>
  );
};

export default Training;
